import { TodoItem } from './adhd-todo-manager';
import { logger } from './logger';

export interface BodyDoublingSession {
  id: string;
  taskId: string;
  task: TodoItem;
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
  checkInIntervalMinutes: number;
  lastCheckInTime?: Date;
  isActive: boolean;
  completedCheckIns: number;
  totalExpectedCheckIns: number;
  encouragementMessages: string[];
  progressNotes: string[];
}

export interface BodyDoublingCheckIn {
  sessionId: string;
  timestamp: Date;
  progressPercent: number;
  isStuck: boolean;
  notes?: string;
  mood: 'focused' | 'distracted' | 'struggling' | 'energized';
  nextSteps?: string[];
}

export class BodyDoublingService {
  private activeSession: BodyDoublingSession | null = null;
  private checkInTimer: NodeJS.Timeout | null = null;
  private sessionsKey = 'adhd-body-doubling-sessions';
  private onCheckInCallback?: (session: BodyDoublingSession) => void;
  private onSessionCompleteCallback?: (session: BodyDoublingSession, wasSuccessful: boolean) => void;

  constructor() {
    this.loadActiveSession();
  }

  /**
   * Start a new body doubling session with a specific task
   */
  startSession(
    task: TodoItem, 
    durationMinutes: number = 25, 
    checkInIntervalMinutes: number = 5
  ): BodyDoublingSession {
    // End any existing session first
    if (this.activeSession) {
      this.endSession(false);
    }

    const now = new Date();
    const session: BodyDoublingSession = {
      id: this.generateSessionId(),
      taskId: task.id,
      task,
      startTime: now,
      endTime: new Date(now.getTime() + durationMinutes * 60000),
      durationMinutes,
      checkInIntervalMinutes,
      isActive: true,
      completedCheckIns: 0,
      totalExpectedCheckIns: Math.floor(durationMinutes / checkInIntervalMinutes),
      encouragementMessages: [],
      progressNotes: []
    };

    this.activeSession = session;
    this.saveActiveSession();
    this.scheduleNextCheckIn();

    logger.adhdAction('body_doubling_started', {
      taskId: task.id,
      taskTitle: task.title,
      durationMinutes,
      checkInIntervalMinutes,
      expectedCheckIns: session.totalExpectedCheckIns
    });

    return session;
  }

  /**
   * End the current body doubling session
   */
  endSession(wasSuccessful: boolean = false): void {
    if (!this.activeSession) return;

    this.activeSession.isActive = false;
    
    if (this.checkInTimer) {
      clearTimeout(this.checkInTimer);
      this.checkInTimer = null;
    }

    const completedSession = { ...this.activeSession };
    this.saveSessionHistory(completedSession);
    
    logger.adhdAction('body_doubling_ended', {
      sessionId: completedSession.id,
      wasSuccessful,
      completedCheckIns: completedSession.completedCheckIns,
      totalExpectedCheckIns: completedSession.totalExpectedCheckIns,
      durationMinutes: completedSession.durationMinutes
    });

    // Notify callback if set
    if (this.onSessionCompleteCallback) {
      this.onSessionCompleteCallback(completedSession, wasSuccessful);
    }

    this.activeSession = null;
    this.clearActiveSession();
  }

  /**
   * Perform a check-in for the current session
   */
  performCheckIn(checkIn: Omit<BodyDoublingCheckIn, 'sessionId' | 'timestamp'>): boolean {
    if (!this.activeSession) return false;

    const now = new Date();
    const fullCheckIn: BodyDoublingCheckIn = {
      ...checkIn,
      sessionId: this.activeSession.id,
      timestamp: now
    };

    this.activeSession.lastCheckInTime = now;
    this.activeSession.completedCheckIns++;
    
    if (fullCheckIn.notes) {
      this.activeSession.progressNotes.push(`${now.toLocaleTimeString()}: ${fullCheckIn.notes}`);
    }

    // Add encouragement based on progress and mood
    const encouragement = this.generateEncouragement(fullCheckIn, this.activeSession);
    if (encouragement) {
      this.activeSession.encouragementMessages.push(encouragement);
    }

    this.saveActiveSession();
    this.scheduleNextCheckIn();

    logger.adhdAction('body_doubling_checkin', {
      sessionId: this.activeSession.id,
      checkInNumber: this.activeSession.completedCheckIns,
      progressPercent: checkIn.progressPercent,
      mood: checkIn.mood,
      isStuck: checkIn.isStuck
    });

    // Notify callback if set
    if (this.onCheckInCallback) {
      this.onCheckInCallback(this.activeSession);
    }

    return true;
  }

  /**
   * Get the current active session
   */
  getCurrentSession(): BodyDoublingSession | null {
    return this.activeSession;
  }

  /**
   * Check if a session is currently active
   */
  isSessionActive(): boolean {
    return this.activeSession?.isActive ?? false;
  }

  /**
   * Get remaining time in the current session
   */
  getRemainingTime(): number {
    if (!this.activeSession) return 0;
    const now = new Date();
    const remaining = this.activeSession.endTime.getTime() - now.getTime();
    return Math.max(0, Math.floor(remaining / 60000)); // Return minutes
  }

  /**
   * Get progress percentage of current session
   */
  getSessionProgress(): number {
    if (!this.activeSession) return 0;
    const now = new Date();
    const elapsed = now.getTime() - this.activeSession.startTime.getTime();
    const total = this.activeSession.endTime.getTime() - this.activeSession.startTime.getTime();
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  }

  /**
   * Set callback for check-ins
   */
  onCheckIn(callback: (session: BodyDoublingSession) => void): void {
    this.onCheckInCallback = callback;
  }

  /**
   * Set callback for session completion
   */
  onSessionComplete(callback: (session: BodyDoublingSession, wasSuccessful: boolean) => void): void {
    this.onSessionCompleteCallback = callback;
  }

  /**
   * Extend current session by additional minutes
   */
  extendSession(additionalMinutes: number): boolean {
    if (!this.activeSession) return false;

    this.activeSession.endTime = new Date(
      this.activeSession.endTime.getTime() + additionalMinutes * 60000
    );
    this.activeSession.durationMinutes += additionalMinutes;
    this.activeSession.totalExpectedCheckIns = Math.floor(
      this.activeSession.durationMinutes / this.activeSession.checkInIntervalMinutes
    );

    this.saveActiveSession();
    return true;
  }

  private generateSessionId(): string {
    return `bd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private scheduleNextCheckIn(): void {
    if (!this.activeSession || this.checkInTimer) return;

    const timeUntilNextCheckIn = this.activeSession.checkInIntervalMinutes * 60000;
    
    this.checkInTimer = setTimeout(() => {
      if (this.activeSession && this.activeSession.isActive) {
        // Check if session should end
        const now = new Date();
        if (now >= this.activeSession.endTime) {
          this.endSession(true);
          return;
        }

        // Trigger check-in prompt
        if (this.onCheckInCallback) {
          this.onCheckInCallback(this.activeSession);
        }
      }
      this.checkInTimer = null;
      this.scheduleNextCheckIn(); // Schedule the next check-in
    }, timeUntilNextCheckIn);
  }

  private generateEncouragement(
    checkIn: BodyDoublingCheckIn, 
    session: BodyDoublingSession
  ): string | null {
    const progressMilestone = Math.floor(checkIn.progressPercent / 25) * 25;
    const checkInNumber = session.completedCheckIns;

    if (checkIn.isStuck) {
      const stuckMessages = [
        "It's okay to feel stuck! Take a deep breath and try breaking the task into smaller pieces.",
        "Stuck happens to everyone. What's the tiniest next step you could take?",
        "When ADHD brains get stuck, movement helps! Try standing up or doing a quick stretch.",
        "Remember: progress over perfection. Even small steps count!"
      ];
      return stuckMessages[Math.floor(Math.random() * stuckMessages.length)];
    }

    switch (checkIn.mood) {
      case 'focused':
        if (progressMilestone >= 50) {
          return "You're in the zone! Keep up this amazing focus!";
        }
        return "Great focus! You're building excellent momentum.";

      case 'energized':
        return "Love this energy! Channel it into the task - you've got this!";

      case 'distracted':
        return "Distractions happen! Gently redirect your attention back to the task. You're doing great.";

      case 'struggling':
        return "Struggling is part of the process. You're being brave by staying with it!";

      default:
        if (checkInNumber === 1) {
          return "Great job on your first check-in! You're building a good rhythm.";
        }
        if (progressMilestone >= 75) {
          return "You're almost there! The finish line is in sight!";
        }
        return null;
    }
  }

  private saveActiveSession(): void {
    if (this.activeSession) {
      localStorage.setItem('adhd-active-body-doubling', JSON.stringify(this.activeSession));
    }
  }

  private clearActiveSession(): void {
    localStorage.removeItem('adhd-active-body-doubling');
  }

  private loadActiveSession(): void {
    const stored = localStorage.getItem('adhd-active-body-doubling');
    if (stored) {
      try {
        const session: BodyDoublingSession = JSON.parse(stored);
        
        // Check if session is still valid (not expired)
        const now = new Date();
        const endTime = new Date(session.endTime);
        
        if (session.isActive && now < endTime) {
          this.activeSession = {
            ...session,
            startTime: new Date(session.startTime),
            endTime: new Date(session.endTime),
            lastCheckInTime: session.lastCheckInTime ? new Date(session.lastCheckInTime) : undefined
          };
          this.scheduleNextCheckIn();
        } else {
          // Session expired, clean it up
          this.clearActiveSession();
        }
      } catch (error) {
        console.error('Failed to load active body doubling session:', error);
        this.clearActiveSession();
      }
    }
  }

  private saveSessionHistory(session: BodyDoublingSession): void {
    try {
      const history = this.getSessionHistory();
      history.push(session);
      
      // Keep only last 50 sessions
      const trimmed = history.slice(-50);
      localStorage.setItem(this.sessionsKey, JSON.stringify(trimmed));
    } catch (error) {
      console.error('Failed to save session history:', error);
    }
  }

  private getSessionHistory(): BodyDoublingSession[] {
    try {
      const stored = localStorage.getItem(this.sessionsKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load session history:', error);
      return [];
    }
  }

  /**
   * Get session statistics
   */
  getSessionStats(): {
    totalSessions: number;
    averageDuration: number;
    successRate: number;
    mostProductiveTimeOfDay: string;
  } {
    const history = this.getSessionHistory();
    
    if (history.length === 0) {
      return {
        totalSessions: 0,
        averageDuration: 0,
        successRate: 0,
        mostProductiveTimeOfDay: 'No data'
      };
    }

    const successfulSessions = history.filter(s => 
      s.completedCheckIns >= s.totalExpectedCheckIns * 0.8
    );

    const averageDuration = history.reduce((sum, s) => sum + s.durationMinutes, 0) / history.length;
    const successRate = (successfulSessions.length / history.length) * 100;

    // Find most productive time of day
    const hourCounts: { [hour: number]: number } = {};
    successfulSessions.forEach(session => {
      const hour = new Date(session.startTime).getHours();
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });

    const mostProductiveHour = Object.entries(hourCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0];

    const mostProductiveTimeOfDay = mostProductiveHour 
      ? `${mostProductiveHour}:00` 
      : 'No data';

    return {
      totalSessions: history.length,
      averageDuration: Math.round(averageDuration),
      successRate: Math.round(successRate),
      mostProductiveTimeOfDay
    };
  }
}

// Create singleton instance
export const bodyDoublingService = new BodyDoublingService();